import DateHelper from 'utils/helper/date-helper';
import { BANK_LIST } from 'utils/enumerator/enum-bank-code';
import { Steps } from 'utils/enumerator/enum-unified-application-underwriting-steps';

export function emptyLoanInitializer() {
  return {
    application: {
      logs: {},
      payment: {
        dueDay: null,
        method: null,
      },
      personal: {
        id: null,
        sex: null,
        name: '',
        taxId: '',
        idIssuer: '',
        idNumber: null,
        birthDate: null,
        birthState: '',
        motherName: '',
        civilStatus: null,
        idIssueDate: null,
        nationality: 55,
        emailAddress: null,
        idIssueLocality: null,
        mobilePhoneNumber: '',
        mobilePhoneBrand: null,
        bestTimeForContact: null,
        extraPhoneNumber: null,
      },
      financial: {
        property: null,
        principal: null,
        bankAccount: {
          kind: null,
          number: null,
          bankCode: null,
          checkDigit: '',
          branchNumber: null,
        },
        informedDefault: null,
        politicallyExposedPerson: null,
        insuranceRequested: false,
      },
      updatedAt: null,
      employment: {
        kind: null,
        payDay: null,
        address: '',
        employer: '',
        position: '',
        phoneNumber: null,
        monthlyIncomeRange: null,
        monthlyIncome: null,
      },
      finishedAt: null,
      cancelledAt: null,
      residential: {
        city: '',
        state: '',
        zipCode: null,
        typeName: null,
        complement: '',
        phoneNumber: null,
        neighborhood: '',
        streetAddress: '',
        streetNumber: '',
      },
      pendingInformation: [],
      pendingSteps: [],
      conditionSelectedAt: null,
      conditionsCalculatedAt: null,
      shortApplication: false,
      pendingApplicationSteps: [],
    },
    state: {
      id: null,
      phase: {
        id: null,
      },
    },
  };
}

export function getFirstName(loan) {
  if (loan?.application) {
    return loan.application.personal.name ? loan.application.personal.name.split(' ')[0] : '';
  }
  return '';
}

export function getPrincipal(loan) {
  if (loan?.conditions) {
    const principal = loan?.conditions?.principal?.toFixed(2)?.toString()?.replace('.', ',');
    return 'R$ ' + principal || '-';
  }
  return '-';
}

export function getBranchNumber(loan) {
  return loan?.application?.financial?.bankAccount?.branchNumber || '-';
}

export function getBankAccountNumber(loan) {
  return (
    `${loan?.application?.financial?.bankAccount?.number}-${loan?.application?.financial?.bankAccount?.checkDigit}` ||
    '-'
  );
}

export function getBankName(loan) {
  const bankName = BANK_LIST.filter((item) => item.code == loan?.application?.financial?.bankAccount?.bankCode);
  const formattedBankName = bankName[0]?.name?.replace(/[0-9]/g, '')?.replace('-', '');
  return formattedBankName || '-';
}

export function getOpenFinanceConsentState(loan) {
  return `${loan?.application?.openFinance?.consentState}` || '-';
}

export function getInstallmentNumber(loan) {
  if (loan?.conditions) {
    return loan?.conditions?.term || '';
  }
  return '';
}

export function getInstallmentPayment(loan) {
  if (loan?.conditions) {
    return loan?.conditions?.installmentsPayment?.toFixed(2)?.toString()?.replace('.', ',') || '';
  }
  return '';
}

export function getInstallments(loan) {
  if (loan?.application) {
    const term = getInstallmentNumber(loan);
    const installmentValue = getInstallmentPayment(loan);
    return term && installmentValue ? term + 'x R$ ' + installmentValue : '-';
  }
  return '-';
}

export function getFirstDueDate(loan) {
  if (loan?.application) {
    return loan.conditions?.firstInstallment?.dueDate
      ? DateHelper.getFormattedDate(loan.conditions.firstInstallment.dueDate)
      : '-';
  }
  return '-';
}

export function getLastDueDate(loan) {
  if (loan?.application) {
    return loan.conditions?.lastInstallment?.dueDate
      ? DateHelper.getFormattedDate(loan.conditions.lastInstallment.dueDate)
      : '-';
  }
  return '-';
}

export function getOneTimeFee(loan) {
  if (loan?.conditions) {
    return `R$ ${loan.conditions?.oneTimeFee?.toFixed(2).toString().replace('.', ',')}`;
  }
  return '-';
}

export function getMonthlyInterestRate(loan) {
  if (loan?.conditions) {
    return `${loan.conditions?.monthlyInterestRate?.toString().replace('.', ',')}% ao mês` || '-';
  }
  return '-';
}

export function getTaxes(loan) {
  if (loan?.conditions) {
    return `R$ ${loan.conditions?.taxes?.toFixed(2).toString().replace('.', ',')}` || '-';
  }
  return '-';
}

export function getEffectiveMonthlyInterestRate(loan) {
  if (loan?.conditions) {
    return `${loan.conditions.effectiveMonthlyInterestRate?.toString().replace('.', ',')}% ao mês`;
  }
  return '-';
}

export function getOfferCardValue(loan) {
  const offerCardValue = [
    {
      label: 'Valor pré-aprovado',
      value: getPrincipal(loan),
    },
    {
      label: 'Valor das parcelas',
      value: getInstallments(loan),
    },
    {
      label: 'Primeiro pagamento',
      value: getFirstDueDate(loan),
    },
  ];

  return offerCardValue;
}

export function getOfferCardValueWithCounterOffer(loan, oldContractValues) {
  const offerCardValue = [
    {
      label: 'Valor pré-aprovado',
      value: getPrincipal(loan),
    },
    {
      label: 'Valor das parcelas',
      value: getInstallments(loan),
    },
    {
      label: 'Primeiro pagamento',
      value: getFirstDueDate(loan),
    },
    {
      label: 'oldValuePrincipal',
      value: oldContractValues && loan?.conditions?.principal != oldContractValues.principal?.value ? getOldValuePrincipal(oldContractValues) : '',
    },
  ];

  return offerCardValue;
}

function getOldValuePrincipal(oldContractValues) {
  return oldContractValues.principal.formatted;
}

function getOldValueInstallmentNumber(oldContractValues) {
  return oldContractValues.term.value;
}

function getOldValueInstallmentPayment(oldContractValues) {
  return oldContractValues.installmentsPayment.formatted;
}

function getOldValueOneTimeFee(oldContractValues) {
  return oldContractValues.oneTimeFee.formatted;
}

function getOldValueFirstDueDate(oldContractValues) {
  return oldContractValues.firstDueDate.formatted;
}

function getOldValueLastDueDate(oldContractValues) {
  return oldContractValues.lastDueDate.formatted;
}

function getOldValueMonthlyInterestRate(oldContractValues) {
  return oldContractValues.monthlyInterestRate.formatted;
}

function getOldValueTaxes(oldContractValues) {
  return oldContractValues.taxes.formatted;
}

function getOldValueEffectiveMonthlyInterestRate(oldContractValues) {
  return oldContractValues.effectiveMonthlyInterestRate.formatted;
}

function showOldValuePrincipal(loan, oldContractValues) {
  return loan.conditions && oldContractValues ? getPrincipal(loan) !== getOldValuePrincipal(oldContractValues) : false;
}

function showOldValueInstallmentNumber(loan, oldContractValues) {
  return loan.conditions && oldContractValues
    ? getInstallmentNumber(loan) !== getOldValueInstallmentNumber(oldContractValues)
    : false;
}

function showOldValueInstallmentPayment(loan, oldContractValues) {
  return loan.conditions && oldContractValues
    ? loan.conditions.installmentsPayment !== oldContractValues.installmentsPayment.value
    : false;
}

function showOldValueOneTimeFee(loan, oldContractValues) {
  return loan.conditions && oldContractValues
    ? loan.conditions.oneTimeFee !== oldContractValues.oneTimeFee.value
    : false;
}

function showOldValueFirstDueDate(loan, oldContractValues) {
  return loan.conditions?.firstInstallment?.dueDate && oldContractValues
    ? loan.conditions.firstInstallment.dueDate !== oldContractValues.firstDueDate.value
    : false;
}

function showOldValueLastDueDate(loan, oldContractValues) {
  return loan.conditions?.lastInstallment?.dueDate && oldContractValues
    ? loan.conditions.lastInstallment.dueDate !== oldContractValues.lastDueDate.value
    : false;
}

function showOldValueMonthlyInterestRate(loan, oldContractValues) {
  return loan.conditions && oldContractValues
    ? loan.conditions.monthlyInterestRate !== oldContractValues.monthlyInterestRate.value
    : false;
}

function showOldValueTaxes(loan, oldContractValues) {
  return loan.conditions && oldContractValues ? loan.conditions.taxes !== oldContractValues.taxes.value : false;
}

function showOldValueEffectiveMonthlyInterestRate(loan, oldContractValues) {
  return loan.conditions && oldContractValues
    ? loan.conditions.effectiveMonthlyInterestRate !== oldContractValues.effectiveMonthlyInterestRate.value
    : false;
}

export function getOfferDetailsValue(
  loan,
  oldContractValues = null,
  firstDueDateElement = 'Data da 1º parcela',
  couponElement = '',
  couponValue = '',
) {
  const offerDetailsValue = [
    {
      id: 1,
      label: 'Valor ofertado',
      value: getPrincipal(loan),
      showOldValue: showOldValuePrincipal(loan, oldContractValues),
      oldValue: oldContractValues ? getOldValuePrincipal(oldContractValues) : '-',
    },
    {
      id: 2,
      label: 'Números de parcelas',
      value: `${getInstallmentNumber(loan)} vezes`,
      showOldValue: showOldValueInstallmentNumber(loan, oldContractValues),
      oldValue: oldContractValues ? `${getOldValueInstallmentNumber(oldContractValues)} vezes` : '-',
    },
    {
      id: 3,
      label: 'Valor das parcelas',
      value: getInstallments(loan),
      showOldValue: showOldValueInstallmentPayment(loan, oldContractValues),
      oldValue: oldContractValues ? getOldValueInstallmentPayment(oldContractValues) : '-',
    },
    {
      id: 4,
      label: 'Taxa única de cadastro (TAC)',
      value: getOneTimeFee(loan),
      showOldValue: showOldValueOneTimeFee(loan, oldContractValues),
      oldValue: oldContractValues ? getOldValueOneTimeFee(oldContractValues) : '-',
    },
    {
      id: 5,
      label: firstDueDateElement,
      value: getFirstDueDate(loan),
      showOldValue: showOldValueFirstDueDate(loan, oldContractValues),
      oldValue: oldContractValues ? getOldValueFirstDueDate(oldContractValues) : '-',
    },
    {
      id: 6,
      label: 'Data da última parcela',
      value: getLastDueDate(loan),
      showOldValue: showOldValueLastDueDate(loan, oldContractValues),
      oldValue: oldContractValues ? getOldValueLastDueDate(oldContractValues) : '-',
    },
    {
      id: 7,
      label: 'Juros',
      value: getMonthlyInterestRate(loan),
      showOldValue: showOldValueMonthlyInterestRate(loan, oldContractValues),
      oldValue: oldContractValues ? getOldValueMonthlyInterestRate(oldContractValues) : '-',
    },
    {
      id: 8,
      label: 'IOF',
      value: getTaxes(loan),
      showOldValue: showOldValueTaxes(loan, oldContractValues),
      oldValue: oldContractValues ? getOldValueTaxes(oldContractValues) : '-',
    },
    {
      id: 9,
      label: 'Custo efetivo total (CET)',
      value: getEffectiveMonthlyInterestRate(loan),
      showOldValue: showOldValueEffectiveMonthlyInterestRate(loan, oldContractValues),
      oldValue: oldContractValues ? getOldValueEffectiveMonthlyInterestRate(oldContractValues) : '-',
    },
    {
      id: 10,
      label: couponElement,
      value: couponValue,
    },
  ];

  return offerDetailsValue;
}

export function loanHasAnyTestTag(tags, loan) {
  const multivariateTests = loan?.labels2?.multivariateTests;
  if (multivariateTests && Array.isArray(multivariateTests)) {
    for (const tag of tags) {
      if (multivariateTests.includes(tag)) {
        return true;
      }
    }
  }
  return false;
}

export function loanHasTestTag(tag, loan) {
  if (loan?.labels2) {
    const { labels2 } = loan;
    return (
      labels2?.multivariateTests && Array.isArray(labels2.multivariateTests) && labels2.multivariateTests.includes(tag)
    );
  }
}

export function loanHasApplicationTestKey(key, loan) {
  return !!loan?.application?.multivariateTests?.[key];
}

export function loanHasApplicationTestTag(key, tag, loan) {
  return loan?.application?.multivariateTests?.[key] === tag;
}

export function loanHasAnyApplicationTestTags(key, tags, loan) {
  if (loanHasApplicationTestKey(key, loan)) {
    for (const tag of tags) {
      if (loanHasApplicationTestTag(key, tag, loan)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * @param {*} loan
 * @param {*} step the step you want to check
 * @returns true if loan is only pending that specific step
 */
export function loanHasOnlyOneSpecificPendingStep(loan, step) {
  return (
    loan?.application?.pendingApplicationSteps?.includes(step) &&
    loan?.application?.pendingApplicationSteps?.length === 1
  );
}

export function loanHasEbsTest(loan) {
  const ebsTestKey = '2023-12-ebsUpsellLabelTest';
  const isEbsTest = loanHasApplicationTestKey(ebsTestKey, loan);

  return loan?.conditions?.counterOffer && isEbsTest;
}

export function loanNeedShowCounterOfferFirst(loan) {
  const ebsTestKey = '2023-12-ebsUpsellLabelTest';
  return loanHasApplicationTestTag(ebsTestKey, 'show-counter-offer', loan) ?? false;
}

/**
 * @param {*} loan
 * @param {*} steps the STEP or ARRAY of STEPS you want to check
 * @returns true if loan has those steps pending
 */
export function loanHasPendingStep(loan, steps) {
  const pendingSteps = loan?.application?.pendingApplicationSteps;

  if (Array.isArray(steps)) {
    return steps.some((step) => pendingSteps.includes(step));
  }

  return pendingSteps.includes(steps);
}

export function hasBiometryAsMandatory(loan) {
  const applicationSteps = loan?.application?.applicationSteps;
  if (applicationSteps === null || applicationSteps === undefined) {
    return false;
  }
  return applicationSteps.some(steps => steps.length === 1 && steps[0] == Steps.BIOMETRY_IDENTITY)
}

/**
 * @param {*} loan
 * @param {*} steps the STEP or ARRAY of STEPS you want to check
 * @returns true if loan has those steps completed
 */
export function loanHasCompletedStep(loan, steps) {
  const completedSteps = loan?.application?.completedApplicationSteps;
  if (Array.isArray(steps)) {
    return steps.some((step) => completedSteps.includes(step));
  }

  return completedSteps.includes(steps);
}

/**
 * @param {*} loan
 * @param {*} possibleSteps the ARRAY of POSSIBLE STEPS you want
 * @returns step the first STEP if pending in the given array
 *
 * Helper function that allows the use of switch case inside routing
 */
export function findFirstPendingStep(loan, possibleSteps) {
  for (const step of possibleSteps) {
    if (loanHasPendingStep(loan, step)) {
      return step;
    }
  }
}

export function shouldShowMultiOfferScreen(loan) {
  if (isRecurring(loan)) {
    return false;
  }


  const applicationSteps = loan?.application?.applicationSteps;
  if (applicationSteps === null || applicationSteps === undefined) {
    return false;
  }
  let multiOfferConfigured = false;
  for (const setOfSteps of applicationSteps) {
    if (setOfSteps.includes(8) && setOfSteps.length > 1) {
      multiOfferConfigured = true;
    }
  }
  return multiOfferConfigured && loan?.conditions?.selectedCollateral === null;
}

export function shouldRedirectToFailedDisbursement(loan) {
  return loan?.application?.multivariateTests?.['2025-04-All-formalizationFailedFlow'] === 'new';
}

export function isMultiOffer(loan) {
  return loan?.conditions?.multiOffer === true;
}

export function isRecurring(loan) {
  return loan?.labels2?.recurrency === 'Recurring';
}

export function loanHasProductTypeEqualTo(loan, productType) {
  return loan?.labels2?.product === productType;
}

export function formalizationErrorNeedAccountChange(loan) {
  const formalizationErrors = loan?.formalization?.financial?.formalizationErrors;

  if (formalizationErrors) {
    const needAccountChangeErrors = [
      'Número da conta transacional do PSP do Recebedor inexistente ou inválido.',
      'Tipo de conta transacional do PSP do Recebedor inexistente ou inválido.',
      'Tipo de Conta do favorecido não permite receber PIX',
      'Tipo de transação não é suportada/autorizada nessa conta.',
      'CPF/CNPJ do usuário recebedor incorreto.',
      'CPF/CNPJ do usuário recebedor não é consistente com o titular da conta creditada.',
      'Erro com o fornecedor QiTech: A conta de destino não suporta este tipo de transação.',
      'Erro com o fornecedor QiTech: CPF/CNPJ da conta de destino está incorreto.',
      'Erro com o fornecedor QiTech: Número da conta de destino é inexistente ou inválido.',
      'Erro com o fornecedor QiTech: A conta de destino encontra-se encerrada.',
      'Erro com o fornecedor QiTech: A conta de destino encontra-se bloqueada.',
      'AB-Número da agência e / ou conta transacional do usuário recebedor inexistente ou inválido',
      'AB-Tipo de transação não é suportado/autorizado na conta transacional especificada; Exemplo: transferência para conta salário',
      'AB-CPF/CNPJ do usuário recebedor incorreto',
      'AB-Banco / ISBP conta destino inválido.',
      'AB-CPF/CNPJ do usuário recebedor não é consistente com o titular da conta transacional especificada',
      'AB-A conta transacional especificada encontra-se bloqueada',
      'AB-Número da conta transacional encerrada no PSP do Recebedor',
      'AB-Agência da conta destino deve ter até 4 caracteres.',
      'Dígito da Conta deve ser numérico',
      'do banco não é valido para criação de proposta',
    ];

    const finded = formalizationErrors.find((formalizationError) =>
      needAccountChangeErrors.find((error) => formalizationError.includes(error)),
    );

    if (finded) return true;
  }
  return false;
}

export function getConditions(loan) {
  return [
    {
      label: 'Instale para receber',
      value: getPrincipal(loan),
    },
    {
      label: 'Parcelas',
      value: getInstallments(loan),
    },
    {
      label: 'Vencimento',
      value: getFirstDueDate(loan),
    },
  ];
}
